import React, { ReactNode, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import { AffiliateTheme } from "../../../affiliate/types";
import { authRuntimeConfig } from "../../../authConfig";

interface AffiliateAuthShellProps {
  theme: AffiliateTheme;
  title: string;
  children: ReactNode;
  /** "preview" renders inline (admin live preview) instead of as a full page. */
  variant?: "page" | "preview";
}

/**
 * Branded frame for native sign-in. Colours come from the surrounding ThemeProvider; this component
 * only places the logo, copy and links. The heading takes focus when the step changes so screen
 * readers announce each new step.
 */
export function AffiliateAuthShell({ theme, title, children, variant = "page" }: AffiliateAuthShellProps) {
  const headingRef = useRef<HTMLElement | null>(null);
  const isFirstTitle = useRef(true);
  const isPage = variant === "page";

  useEffect(() => {
    if (isFirstTitle.current) {
      isFirstTitle.current = false;
      return;
    }
    if (isPage) {
      headingRef.current?.focus();
    }
  }, [title, isPage]);

  return (
    <Box
      component={isPage ? "main" : "div"}
      sx={{
        minHeight: isPage ? "100vh" : undefined,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      {/* Same treatment as the app's navigation bar, so sign-in looks like part of the product. */}
      <Box
        component="header"
        sx={{
          bgcolor: "primary.main",
          color: "#FFFFFF",
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {theme.logoUrl ? (
          // A wordmark would repeat the name, so the logo replaces it. The plate keeps a dark
          // logo visible on the primary colour.
          <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 1, px: 1, py: 0.5, display: "flex" }}>
            <Box
              component="img"
              src={theme.logoUrl}
              alt={theme.name}
              // The same slot as the app bar, so a wide logo reads the same on both.
              sx={{ height: { xs: 36, sm: 48 }, maxWidth: { xs: 160, sm: 320 }, objectFit: "contain" }}
            />
          </Box>
        ) : (
          <Typography variant="h6" component="p" noWrap sx={{ fontWeight: 700, letterSpacing: "0.5px" }}>
            {theme.name}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: isPage ? 6 : 3,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          {theme.tagline && (
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}>
              {theme.tagline}
            </Typography>
          )}

          <Paper
            elevation={0}
            sx={{ p: { xs: 3, sm: 4 }, border: 1, borderColor: "divider", bgcolor: "background.paper" }}
          >
            <Typography
              ref={(element: HTMLElement | null) => {
                headingRef.current = element;
              }}
              tabIndex={-1}
              component={isPage ? "h1" : "h2"}
              variant="h5"
              sx={{ mb: 2, fontWeight: 600, "&:focus": { outline: "none" } }}
            >
              {title}
            </Typography>
            {children}
          </Paper>

          <Box
            component="footer"
            sx={{ mt: 3, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 2 }}
          >
            {theme.termsUrl && (
              <Link href={theme.termsUrl} target="_blank" rel="noopener noreferrer" color="text.secondary" variant="body2">
                Terms of use
              </Link>
            )}
            {theme.supportEmail && (
              <Link href={`mailto:${theme.supportEmail}`} color="text.secondary" variant="body2">
                Contact support
              </Link>
            )}
            <Typography variant="body2" sx={{ color: "text.secondary", width: "100%", textAlign: "center" }}>
              Powered by {authRuntimeConfig.productName}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const centeredPage = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  px: 2,
  textAlign: "center",
} as const;

/** Visible, announced progress state, so the sign-in route is never a blank page. */
export function AuthStatusScreen({ message, children }: { message: string; children?: ReactNode }) {
  return (
    <Box component="main" sx={centeredPage}>
      {/* Deliberately neutral: until the affiliate is known, product colours here would read as
          the wrong brand flashing before theirs. */}
      <Box role="status" aria-live="polite" sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <CircularProgress size={48} aria-hidden="true" sx={{ color: "grey.500" }} />
        <Typography variant="h6" component="p" sx={{ color: "grey.700" }}>
          {message}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

export function AuthErrorScreen({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Box component="main" sx={centeredPage}>
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Sign in
        </Typography>
        <Alert severity="error" role="alert" sx={{ mb: 2, textAlign: "left" }}>
          {message}
        </Alert>
        <Button variant="contained" size="large" fullWidth onClick={onAction}>
          {actionLabel}
        </Button>
      </Box>
    </Box>
  );
}
